import { getCategoryProfile } from "../category-profiles";
import { validateCategoryTaxonomy, type CategoryTaxonomyConfig, type CategoryTaxonomyNode, type CategoryTaxonomyNodeStatus } from "../category-taxonomy";
import type { IntakeCategoryId, IntakeDomain } from "../types";
import { INTAKE_DOMAIN_IDS } from "../../../shared/taxonomy";

export function formatCategoryTaxonomyBlockingMessage(diagnostics: ReturnType<typeof validateCategoryTaxonomy>): string {
  const details = diagnostics.slice(0, 4).map((diagnostic) => diagnostic.message);
  const suffix = diagnostics.length > details.length ? ` 외 ${diagnostics.length - details.length}개` : "";
  return `저장하기 전에 ${diagnostics.length}개 항목을 확인해주세요: ${details.join(" / ")}${suffix}`;
}

export function CategoryTaxonomyDiagnosticsList({ diagnostics }: { diagnostics: ReturnType<typeof validateCategoryTaxonomy> }) {
  return (
    <div className="admin-diagnostic-list">
      {diagnostics.length === 0 ? (
        <div className="admin-alert success">검증 오류가 없습니다.</div>
      ) : diagnostics.map((diagnostic) => (
        <div className={`admin-alert ${diagnostic.level === "error" ? "error" : "warning"}`} key={`${diagnostic.code}-${diagnostic.nodeId ?? "global"}-${diagnostic.message}`}>
          <strong>{diagnostic.code}</strong> {diagnostic.message}
        </div>
      ))}
    </div>
  );
}

// 서버에 저장된 카테고리 설정 기준점. missing은 아직 한 번도 저장되지 않은 상태다.
export type SavedTaxonomyState =
  | { status: "loading" }
  | { status: "missing" }
  | { status: "saved"; config: CategoryTaxonomyConfig; updatedAt: string };

// 조회 전이거나 조회에 실패한 동안에는 저장 여부를 단정하지 않는다.
export function hasUnsavedTaxonomyChanges(savedState: SavedTaxonomyState, normalizedConfig: CategoryTaxonomyConfig): boolean {
  if (savedState.status === "loading") return false;
  if (savedState.status === "missing") return true;
  return JSON.stringify(savedState.config.nodes) !== JSON.stringify(normalizedConfig.nodes);
}

const STATUS_LABELS: Record<CategoryTaxonomyNodeStatus, string> = { active: "공개", hidden: "비공개", deprecated: "삭제됨" };

export function statusLabel(status: CategoryTaxonomyNodeStatus): string {
  return STATUS_LABELS[status];
}

export function splitLines(value: string): string[] {
  return Array.from(new Set(value.split("\n").map((line) => line.trim()).filter(Boolean)));
}

export function inferDomainForGroup(group: CategoryTaxonomyNode, config: CategoryTaxonomyConfig): Exclude<IntakeDomain, ""> {
  const idDomain = group.id.replace(/^group\./, "");
  if (INTAKE_DOMAIN_IDS.includes(idDomain as Exclude<IntakeDomain, "">)) return idDomain as Exclude<IntakeDomain, "">;
  const childProfile = config.nodes.find((node) => node.kind === "category" && node.parentId === group.id && node.profileId)?.profileId;
  return getCategoryProfile(childProfile)?.domain ?? "partner";
}

export function createCustomProfileId(domain: Exclude<IntakeDomain, "">, config: CategoryTaxonomyConfig): Exclude<IntakeCategoryId, ""> {
  const usedProfileIds = new Set(config.nodes.flatMap((node) => node.profileId ? [node.profileId] : []));
  const seed = Date.now().toString(36);
  for (let index = 0; index < 100; index += 1) {
    const suffix = index === 0 ? seed : `${seed}_${index}`;
    const profileId = `${domain}_custom_${suffix}` as Exclude<IntakeCategoryId, "">;
    if (!usedProfileIds.has(profileId)) return profileId;
  }
  return `${domain}_custom_${crypto.randomUUID().slice(0, 8)}` as Exclude<IntakeCategoryId, "">;
}
